import axios from "axios";
import { Env } from "../enviroment/environment";
import authHeader from "./auth-header";
import {
  readOrendaAuth,
  ensureOrendaTokensFresh,
} from "../components/orenda/orendaAuthStorage";

// Orenda-related calls that go through the Bopayment backend. Every method
// begins with `await ensureOrendaTokensFresh()` — this checks the id_token
// expiry and silently refreshes via the Cognito refresh_token if under a
// 60-second buffer. If refresh fails (refresh_token expired etc.) it throws
// SsoSessionExpired which the calling screen catches to prompt re-auth
// WITHOUT losing form state.
//
// Backend endpoints live in CardHolderController:
//   POST api/cardholder/viewapplicationorenda
//   POST api/cardholder/getkycschemaorenda
//   POST api/cardholder/getlegaldocumentorenda
//   POST api/cardholder/acceptlegaldocumentorenda
//   POST api/cardholder/submitkycdataorenda

const API_URL_Cardholders = Env.CardHolderApiUrl;

// Build the OrendaToken payload the backend expects. When `freshAuth` is
// passed (from ensureOrendaTokensFresh) we use those tokens instead of a
// fresh sessionStorage read — this closes the race where a refresh happens
// mid-call and we'd otherwise send the stale token.
const buildOrendaTokenPayload = (overrides = {}, freshAuth = null) => {
  const auth = freshAuth || readOrendaAuth();
  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  return {
    IdToken: overrides.IdToken ?? auth?.id_token ?? "",
    AccessToken: overrides.AccessToken ?? auth?.access_token ?? "",
    UserId: overrides.UserId ?? user?.user?.UserId ?? "",
    Email: overrides.Email ?? user?.user?.Email ?? "",
  };
};

const viewApplicationOrenda = async (overrides) => {
  const auth = await ensureOrendaTokensFresh();
  const response = await axios.post(
    API_URL_Cardholders + "viewapplicationorenda",
    buildOrendaTokenPayload(overrides, auth),
    { headers: authHeader() }
  );
  return response.data;
};

const getKycSchemaOrenda = async (overrides) => {
  const auth = await ensureOrendaTokensFresh();
  const response = await axios.post(
    API_URL_Cardholders + "getkycschemaorenda",
    buildOrendaTokenPayload(overrides, auth),
    { headers: authHeader() }
  );
  return response.data;
};

// Fetches a Sumsub Web SDK token for the current application. Sumsub's SDK
// takes this as `accessToken` — it's the SDK's per-user session credential
// tied to Orenda's Sumsub configuration. Response wraps { token, expiresAt }
// (millisecond epoch).
const getSumsubTokenOrenda = async (overrides) => {
  const auth = await ensureOrendaTokensFresh();
  const response = await axios.post(
    API_URL_Cardholders + "getsumsubtokenorenda",
    buildOrendaTokenPayload(overrides, auth),
    { headers: authHeader() }
  );
  return response.data;
};

// Creates the applicant's first card after onboarding is complete. Backend
// looks up applicationId / accountId / customerId from the DB using UserId,
// so we only need the token payload. Response wraps CardDataOrenda:
//   { id, customerId, accountId, cardType, status, provider }
const createCardOrenda = async (overrides) => {
  const auth = await ensureOrendaTokensFresh();
  const response = await axios.post(
    API_URL_Cardholders + "createcardorenda",
    buildOrendaTokenPayload(overrides, auth),
    { headers: authHeader() }
  );
  return response.data;
};

// Fetches the applicant's funding account info (IBAN, BIC, AccountId,
// Currency). Shown on the dashboard behind an "Account details" button
// — not sensitive like PAN/CVC, no passkey required.
//
// Backend DTO: Faisal's endpoint currently reads a `userId` query param
// (originally tokenId, renamed). We send the OrendaToken body for its
// AccessToken/UserId AND tack userId onto the query string so the backend
// binds it either way during cutover.
const getAccountDetailsOrenda = async (overrides) => {
  const auth = await ensureOrendaTokensFresh();
  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  const userId = overrides?.UserId ?? user?.user?.UserId ?? "";
  const response = await axios.post(
    API_URL_Cardholders + "getaccountdetails?userId=" + encodeURIComponent(userId),
    buildOrendaTokenPayload(overrides, auth),
    { headers: authHeader() }
  );
  return response.data;
};

// Block / unblock the applicant's Orenda funding account. The backend DTO
// (BlockUnblockAccountOrenda) takes IdToken + AccessToken + UserId plus:
//   • IsBlock = true  → block/suspend the account
//   • IsBlock = false → unblock/reactivate it
//   • Reason          → short free-text reason the admin / user supplied
// which direction we're sending is driven by the account's current `status`
// (returned from getAccountDetails: "active" vs "suspend").
const blockUnblockAccountOrenda = async ({ isBlock, reason } = {}) => {
  const auth = await ensureOrendaTokensFresh();
  const body = {
    ...buildOrendaTokenPayload({}, auth),
    IsBlock: !!isBlock,
    Reason: reason || "",
  };
  const response = await axios.post(
    API_URL_Cardholders + "blockunblockaccountorenda",
    body,
    { headers: authHeader() }
  );
  return response.data;
};

// ─────────────────────────────────────────────────────────────────
// Accounts — list the user's Orenda accounts (balance + IBAN etc.) for
// the dashboard. Backend derives customerId from UserId.
// Docs: https://api-docs.orenda-apps.com/api-reference/accounts/list-accounts/
// ─────────────────────────────────────────────────────────────
const listAccountsOrenda = async ({
  page,
  limit,
  status,
  currency,
} = {}) => {
  const auth = await ensureOrendaTokensFresh();
  const body = {
    ...buildOrendaTokenPayload({}, auth),
    page: page ?? null,
    limit: limit ?? null,
    status: status ?? null,
    currency: currency ?? null,
  };
  const response = await axios.post(
    API_URL_Cardholders + "listaccountsorenda",
    body,
    { headers: authHeader() }
  );
  return response.data;
};

// ─────────────────────────────────────────────────────────────────
// Beneficiaries — payees the cardholder sends money to. Backend derives
// customerId from UserId; frontend just sends UserId + an action-specific
// payload. Create needs a step-up (passkey/totp/pin); other three don't.
// Docs: https://api-docs.orenda-apps.com/api-reference/beneficiaries/
// ─────────────────────────────────────────────────────────────

const listBeneficiariesOrenda = async ({ limit, nextToken } = {}) => {
  const auth = await ensureOrendaTokensFresh();
  const body = {
    ...buildOrendaTokenPayload({}, auth),
    limit: limit ?? null,
    nextToken: nextToken ?? null,
  };
  const response = await axios.post(
    API_URL_Cardholders + "listbeneficiariesorenda",
    body,
    { headers: authHeader() }
  );
  return response.data;
};

const getBeneficiaryOrenda = async ({ beneficiaryId } = {}) => {
  const auth = await ensureOrendaTokensFresh();
  const body = {
    ...buildOrendaTokenPayload({}, auth),
    beneficiaryId,
  };
  const response = await axios.post(
    API_URL_Cardholders + "getbeneficiaryorenda",
    body,
    { headers: authHeader() }
  );
  return response.data;
};

// Caller supplies: name, account:{type,sortCode,accountNumber|iban,bic,bankName},
// confirmation:{method,passkeySession,assertion|totp,accessToken|pin},
// optional address, optional isCorporate, optional idempotencyKey.
// accountId is derived server-side from the user's primary account unless
// the caller passes one explicitly.
const createBeneficiaryOrenda = async ({
  accountId,
  name,
  account,
  confirmation,
  address,
  isCorporate,
  idempotencyKey,
} = {}) => {
  const auth = await ensureOrendaTokensFresh();
  const body = {
    ...buildOrendaTokenPayload({}, auth),
    accountId: accountId ?? null,
    name,
    account,
    confirmation,
    address: address ?? null,
    isCorporate: typeof isCorporate === "boolean" ? isCorporate : null,
    idempotencyKey: idempotencyKey ?? null,
  };
  const response = await axios.post(
    API_URL_Cardholders + "createbeneficiaryorenda",
    body,
    { headers: authHeader() }
  );
  return response.data;
};

const deleteBeneficiaryOrenda = async ({ beneficiaryId } = {}) => {
  const auth = await ensureOrendaTokensFresh();
  const body = {
    ...buildOrendaTokenPayload({}, auth),
    beneficiaryId,
  };
  const response = await axios.post(
    API_URL_Cardholders + "deletebeneficiaryorenda",
    body,
    { headers: authHeader() }
  );
  return response.data;
};

// ─────────────────────────────────────────────────────────────────
// Payments — docs:
//   https://api-docs.orenda-apps.com/api-reference/payments/
//
// Backend resolves customerId + accountId from UserId; frontend only sends
// payment-specific fields. Orenda's docs are explicit about idempotency:
// "Always send an idempotency key" — the backend auto-fills a UUID when
// the frontend omits one, but passing one ourselves (per-attempt) means
// retries from the UI dedupe safely too.
// ─────────────────────────────────────────────────────────────────

// Lightweight UUID v4 for idempotency keys. We don't need cryptographic
// strength — just a random-enough value per attempt so retries dedupe.
const uuid = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    try { return crypto.randomUUID(); } catch { /* fall through */ }
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// Send money. Required: { beneficiaryId, amount, purpose }. Optional:
// currency, reference, scheduledDate (ISO), frequency, chargeBearer,
// debtorViban, endToEndReference, idempotencyKey.
const createPaymentOrenda = async ({
  beneficiaryId,
  amount,
  purpose,
  currency,
  reference,
  endToEndReference,
  chargeBearer,
  debtorViban,
  scheduledDate,
  frequency,
  idempotencyKey,
} = {}) => {
  const auth = await ensureOrendaTokensFresh();
  const body = {
    ...buildOrendaTokenPayload({}, auth),
    beneficiaryId,
    amount,
    purpose,
    currency: currency ?? null,
    reference: reference ?? null,
    endToEndReference: endToEndReference ?? null,
    chargeBearer: chargeBearer ?? null,
    debtorViban: debtorViban ?? null,
    scheduledDate: scheduledDate ?? null,
    frequency: frequency ?? null,
    idempotencyKey: idempotencyKey || uuid(),
  };
  const response = await axios.post(
    API_URL_Cardholders + "createpaymentorenda",
    body,
    { headers: authHeader() }
  );
  return response.data;
};

// In-flight + history. All filters optional; defaults match Orenda's.
const listPaymentRequestsOrenda = async ({
  status,
  type,
  id,
  isDirectDebit,
  currency,
  startDate,
  endDate,
  page,
  limit,
  sortBy,
  sortOrder,
} = {}) => {
  const auth = await ensureOrendaTokensFresh();
  const body = {
    ...buildOrendaTokenPayload({}, auth),
    status: status ?? null,
    type: type ?? null,
    id: id ?? null,
    isDirectDebit: typeof isDirectDebit === "boolean" ? isDirectDebit : null,
    currency: currency ?? null,
    startDate: startDate ?? null,
    endDate: endDate ?? null,
    page: page ?? null,
    limit: limit ?? null,
    sortBy: sortBy ?? null,
    sortOrder: sortOrder ?? null,
  };
  const response = await axios.post(
    API_URL_Cardholders + "listpaymentrequestsorenda",
    body,
    { headers: authHeader() }
  );
  return response.data;
};

// Fee preview — called before the user confirms a payment.
const calculatePaymentFeeOrenda = async ({ amount } = {}) => {
  const auth = await ensureOrendaTokensFresh();
  const body = {
    ...buildOrendaTokenPayload({}, auth),
    amount,
  };
  const response = await axios.post(
    API_URL_Cardholders + "calculatepaymentfeeorenda",
    body,
    { headers: authHeader() }
  );
  return response.data;
};

// Cancel a direct-debit collection request. Only valid while the request
// is INITIATED or PENDING and before the 09:00 Europe/Amsterdam cutoff on
// collection day.
const cancelDirectDebitOrenda = async ({ paymentRequestId } = {}) => {
  const auth = await ensureOrendaTokensFresh();
  const body = {
    ...buildOrendaTokenPayload({}, auth),
    paymentRequestId,
  };
  const response = await axios.post(
    API_URL_Cardholders + "canceldirectdebitorenda",
    body,
    { headers: authHeader() }
  );
  return response.data;
};

// List scheduled payments on the user's account. The create-payment flow
// can set a `scheduledDate` and `frequency`; those live here until they
// run (or until the user cancels them with deleteScheduledPaymentOrenda).
// Orenda's response wraps the array as `data.scheduledPayments`.
const listScheduledPaymentsOrenda = async ({ limit, page } = {}) => {
  const auth = await ensureOrendaTokensFresh();
  const body = {
    ...buildOrendaTokenPayload({}, auth),
    limit: limit ?? null,
    page: page ?? null,
  };
  const response = await axios.post(
    API_URL_Cardholders + "listscheduledpaymentsorenda",
    body,
    { headers: authHeader() }
  );
  return response.data;
};

// Delete (cancel) a scheduled payment. Also cancels any renewal — if the
// schedule came from a batch, the batch item is cancelled with it.
const deleteScheduledPaymentOrenda = async ({ scheduleId } = {}) => {
  const auth = await ensureOrendaTokensFresh();
  const body = {
    ...buildOrendaTokenPayload({}, auth),
    scheduleId,
  };
  const response = await axios.post(
    API_URL_Cardholders + "deletescheduledpaymentorenda",
    body,
    { headers: authHeader() }
  );
  return response.data;
};

// ─────────────────────────────────────────────────────────────────
// Transactions — docs:
//   https://api-docs.orenda-apps.com/api-reference/transactions/
//
// List returns the customer's transactions newest first, 10 per page by
// default. Every filter string accepts comma-separated values so
// `status=COMPLETED,UNPAID` and `currency=GBP,EUR` act as multi-selects.
// Rows the service can't render are dropped from the page AND from `total`
// rather than failing the whole request.
//
// Generate statement returns `{ data: { dataURI: "data:application/pdf;…" }}`
// — the caller can set an <a download> href straight from that.
// ─────────────────────────────────────────────────────────────────

const listTransactionsOrenda = async (filters = {}) => {
  const auth = await ensureOrendaTokensFresh();
  const body = {
    ...buildOrendaTokenPayload({}, auth),
    accountId: filters.accountId ?? null,
    direction: filters.direction ?? null,
    status: filters.status ?? null,
    currency: filters.currency ?? null,
    paymentType: filters.paymentType ?? null,
    reference: filters.reference ?? null,
    payeeName: filters.payeeName ?? null,
    payerName: filters.payerName ?? null,
    isFee: typeof filters.isFee === "boolean" ? filters.isFee : null,
    startDate: filters.startDate ?? null,
    endDate: filters.endDate ?? null,
    page: filters.page ?? null,
    limit: filters.limit ?? null,
    sortBy: filters.sortBy ?? null,
    sortOrder: filters.sortOrder ?? null,
    transactionId: filters.transactionId ?? null,
    paymentId: filters.paymentId ?? null,
    paymentSubType: filters.paymentSubType ?? null,
    endToEndRef: filters.endToEndRef ?? null,
    uniqueReference: filters.uniqueReference ?? null,
    internalReference: filters.internalReference ?? null,
    dateTime: filters.dateTime ?? null,
  };
  const response = await axios.post(
    API_URL_Cardholders + "listtransactionsorenda",
    body,
    { headers: authHeader() }
  );
  return response.data;
};

// Generate a PDF or CSV statement. Required: format ("pdf" | "csv").
// chosenMonth (YYYY-MM) beats year beats { startDate, endDate }.
const generateStatementOrenda = async ({
  format,
  chosenMonth,
  year,
  startDate,
  endDate,
} = {}) => {
  const auth = await ensureOrendaTokensFresh();
  const body = {
    ...buildOrendaTokenPayload({}, auth),
    format,
    chosenMonth: chosenMonth ?? null,
    year: year ?? null,
    startDate: startDate ?? null,
    endDate: endDate ?? null,
  };
  const response = await axios.post(
    API_URL_Cardholders + "generatestatementorenda",
    body,
    { headers: authHeader() }
  );
  return response.data;
};

// ─────────────────────────────────────────────────────────────────
// 3DS — docs:
//   https://api-docs.orenda-apps.com/api-reference/3ds/
//
// Get recent notifications returns up to 5 recent challenges in the
// look-back window (hours, default 24, max 72). Rows carry a status —
// PENDING ones can be confirmed; APPROVED/REJECTED/EXPIRED are terminal.
// Confirm takes { authId, action } where authId is the notification id
// and action is "approve" or "reject".
// ─────────────────────────────────────────────────────────────────

const getRecent3dsNotificationsOrenda = async ({ hours } = {}) => {
  const auth = await ensureOrendaTokensFresh();
  const body = {
    ...buildOrendaTokenPayload({}, auth),
    hours: hours ?? null,
  };
  const response = await axios.post(
    API_URL_Cardholders + "getrecent3dsnotificationsorenda",
    body,
    { headers: authHeader() }
  );
  return response.data;
};

const confirm3dsChallengeOrenda = async ({ authId, action } = {}) => {
  const auth = await ensureOrendaTokensFresh();
  const body = {
    ...buildOrendaTokenPayload({}, auth),
    authId,
    action,
  };
  const response = await axios.post(
    API_URL_Cardholders + "confirm3dschallengeorenda",
    body,
    { headers: authHeader() }
  );
  return response.data;
};

// Fetches the set of legal documents the applicant must review + accept for
// the LEGAL_AGREEMENTS onboarding step. Response.data.documents.docs[] is the
// list to render.
const getLegalDocumentOrenda = async (overrides) => {
  const auth = await ensureOrendaTokensFresh();
  const response = await axios.post(
    API_URL_Cardholders + "getlegaldocumentorenda",
    buildOrendaTokenPayload(overrides, auth),
    { headers: authHeader() }
  );
  return response.data;
};

// Submits the applicant's acceptance of a set of legal documents.
// Backend DTO (AcceptDocumentsOrendaRequest) uses camelCase.
const acceptLegalDocumentOrenda = async ({
  documentSetVersion,
  applicationId,
  acceptedDocuments,
  idTokenOverride,
  userIdOverride,
}) => {
  const auth = await ensureOrendaTokensFresh();
  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  const body = {
    documentSetVersion,
    idToken: idTokenOverride ?? auth?.id_token ?? "",
    userId: userIdOverride ?? user?.user?.UserId ?? "",
    applicationId,
    acceptedDocuments,
  };
  const response = await axios.post(
    API_URL_Cardholders + "acceptlegaldocumentorenda",
    body,
    { headers: authHeader() }
  );
  return response.data;
};

// Submits collected KYC answers to the backend.
// Backend DTO (SubmitKycRequest) — camelCase:
//   kycanswers    nested map grouped by step, matching Orenda's schema shape:
//                 { "step-1": { fieldKey: value, ... }, "step-2": {...}, ... }
//                 Flat top-level keys ("employmentStatus" etc.) fail with
//                 "Step '<key>' does not exist in schema".
//   idToken       Cognito id_token
//   accessToken   Cognito access_token
//   userId        Card-Console UserId
//   applicationId from viewApplication response
const submitKycDataOrenda = async ({
  kycanswers,
  applicationId,
  idTokenOverride,
  accessTokenOverride,
  userIdOverride,
}) => {
  const auth = await ensureOrendaTokensFresh();
  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  const body = {
    kycanswers,
    idToken: idTokenOverride ?? auth?.id_token ?? "",
    accessToken: accessTokenOverride ?? auth?.access_token ?? "",
    userId: userIdOverride ?? user?.user?.UserId ?? "",
    applicationId,
  };
  const response = await axios.post(
    API_URL_Cardholders + "submitkycdataorenda",
    body,
    { headers: authHeader() }
  );
  return response.data;
};

// ─────────────────────────────────────────────────────────────────
// Secure card details — Orenda's two-step (or three-step with
// passkey) flow for revealing PAN / CVC / expiry. Docs:
//   https://api-docs.orenda-apps.com/api-reference/secure-details/get-secure-key/
//
// Step 1: getPassKeySessionOrenda → backend wraps Orenda's /secure-key
//   (and may chain /passkey-challenge) and returns the cipher plus
//   WebAuthn fido2options we feed to navigator.credentials.get().
// Step 2: user approves the passkey → produces a WebAuthn assertion
// Step 3: getCardSecureDetailsOrenda → sends cipher + passkeySession
//   + assertion and gets back the actual card details.
// ─────────────────────────────────────────────────────────────────

const getPassKeySessionOrenda = async (overrides) => {
  const auth = await ensureOrendaTokensFresh();
  const response = await axios.post(
    API_URL_Cardholders + "getpasskeysession",
    buildOrendaTokenPayload(overrides, auth),
    { headers: authHeader() }
  );
  return response.data;
};

// Non-card step-up — for create-beneficiary and create-payment. Backend
// hits Orenda's generic v2/auth/passkey/challenge without chaining the
// card /secure-key call (which 403s GET_CUSTOMER_SECURE_KEY_FAILED when
// the user doesn't have an active card-reveal context). Response shape
// is the same (passkeySession + fido2options), so callers parse with
// parsePassKeySessionResponse unchanged.
const getStepUpPassKeySessionOrenda = async (overrides) => {
  const auth = await ensureOrendaTokensFresh();
  const response = await axios.post(
    API_URL_Cardholders + "getstepuppasskeysession",
    buildOrendaTokenPayload(overrides, auth),
    { headers: authHeader() }
  );
  return response.data;
};

// ─────────────────────────────────────────────────────────────────
// Passkey enrollment — adds a new passkey to the signed-in user so
// they can confirm sensitive actions (viewing PAN/CVC). SSO users:
// the first startAddPasskey call returns EMAIL_VERIFICATION_REQUIRED
// and Orenda emails a 6-digit code; the second call takes the code.
// Then the browser does navigator.credentials.create() and we send
// the attestation to finishAddPasskey.
// ─────────────────────────────────────────────────────────────────

// v2 field names: friendlyName / emailCode (camelCase). Our backend DTO
// accepts both v1 and v2 for the brief cutover window, but we always send
// v2 going forward.
const startAddPasskeyOrenda = async ({ friendlyName, emailCode } = {}) => {
  const auth = await ensureOrendaTokensFresh();
  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  const body = {
    AccessToken: auth?.access_token ?? "",
    UserId: user?.user?.UserId ?? "",
    friendlyName: friendlyName || null,
    emailCode: emailCode || null,
  };
  const response = await axios.post(
    API_URL_Cardholders + "startaddpasskeyorenda",
    body,
    { headers: authHeader() }
  );
  return response.data;
};

// v2 shape: body now takes `sessionToken` and the pre-flattened `attestation`
// ({ credentialIdB64, clientDataJSON_B64, attestationObjectB64, transports }).
const finishAddPasskeyOrenda = async ({ sessionToken, credential }) => {
  const auth = await ensureOrendaTokensFresh();
  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  const body = {
    AccessToken: auth?.access_token ?? "",
    UserId: user?.user?.UserId ?? "",
    sessionToken,
    attestation: credential,
  };
  const response = await axios.post(
    API_URL_Cardholders + "finishaddpasskeyorenda",
    body,
    { headers: authHeader() }
  );
  return response.data;
};

const listPasskeysOrenda = async () => {
  const auth = await ensureOrendaTokensFresh();
  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  const body = {
    AccessToken: auth?.access_token ?? "",
    UserId: user?.user?.UserId ?? "",
  };
  const response = await axios.post(
    API_URL_Cardholders + "listpasskeysorenda",
    body,
    { headers: authHeader() }
  );
  return response.data;
};

// Backend DTO (GetSecureDetailsOrenda) — camelCase:
//   AccessToken, UserId, cipher, assertion, passkeySession, secretKey
// secretKey is the AES key the backend uses to decrypt pan/cvc/expiry
// before persisting. FE still has its own copy and still decrypts
// client-side for display.
const getCardSecureDetailsOrenda = async ({
  cipher,
  assertion,
  passkeySession,
  secretKey,
  accessTokenOverride,
  userIdOverride,
}) => {
  const auth = await ensureOrendaTokensFresh();
  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  const body = {
    AccessToken: accessTokenOverride ?? auth?.access_token ?? "",
    UserId: userIdOverride ?? user?.user?.UserId ?? "",
    cipher,
    assertion,
    passkeySession,
    secretKey,
  };
  const response = await axios.post(
    API_URL_Cardholders + "getcardsecuredetails",
    body,
    { headers: authHeader() }
  );
  return response.data;
};

const orendaService = {
  viewApplicationOrenda,
  getKycSchemaOrenda,
  getSumsubTokenOrenda,
  getLegalDocumentOrenda,
  acceptLegalDocumentOrenda,
  submitKycDataOrenda,
  createCardOrenda,
  getPassKeySessionOrenda,
  getStepUpPassKeySessionOrenda,
  getCardSecureDetailsOrenda,
  getAccountDetailsOrenda,
  blockUnblockAccountOrenda,
  listAccountsOrenda,
  listBeneficiariesOrenda,
  getBeneficiaryOrenda,
  createBeneficiaryOrenda,
  deleteBeneficiaryOrenda,
  createPaymentOrenda,
  listPaymentRequestsOrenda,
  calculatePaymentFeeOrenda,
  cancelDirectDebitOrenda,
  listScheduledPaymentsOrenda,
  deleteScheduledPaymentOrenda,
  listTransactionsOrenda,
  generateStatementOrenda,
  getRecent3dsNotificationsOrenda,
  confirm3dsChallengeOrenda,
  startAddPasskeyOrenda,
  finishAddPasskeyOrenda,
  listPasskeysOrenda,
  buildOrendaTokenPayload,
};

export default orendaService;
